import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';

const WORKFLOW_STEPS = [
  'Booking',
  'Finance & Document Verification',
  'Old Car Valuation & Exchange Check',
  'Showroom Verification & Accessories Check',
  'RTO Registration Process',
  'Insurance Binding',
  'Down Payment Verification',
  'Allotment',
  'PDI & Invoicing',
  'Delivery'
];

const statCards = [
  { key: 'totalLeads',           label: 'Total Leads',      color: 'var(--crm-primary-light)',  textColor: 'var(--crm-primary)', icon: '📈' },
  { key: 'newLeads',             label: 'New Leads',         color: 'var(--crm-info-light)',     textColor: 'var(--crm-info)', icon: '🌟' },
  { key: 'convertedLeads',       label: 'Converted Leads',   color: 'var(--crm-success-light)',  textColor: 'var(--crm-success)', icon: '✅' },
  { key: 'showroomBookings',     label: 'Showroom Bookings', color: '#E0E7FF',                   textColor: '#4338CA', icon: '🚗' },
  { key: 'showroomDeliveries',   label: 'Showroom Deliveries', color: '#D1FAE5',                 textColor: '#065F46', icon: '🎁' },
  { key: 'totalCustomers',       label: 'Active Customers',  color: 'var(--crm-warning-light)',  textColor: '#92400E', icon: '👥' },
  { key: 'pendingWorkflowSteps', label: 'Pending Steps',     color: 'var(--crm-danger-light)',   textColor: 'var(--crm-danger)', icon: '⏳' },
  { key: 'holdSteps',            label: 'On Hold',           color: '#FEE2E2',                   textColor: '#991B1B', icon: '🛑' },
];

let overviewStatsCache: any = null;

const AdminOverview: React.FC = () => {
  const [stats, setStats] = useState<Record<string, number>>(overviewStatsCache?.stats || {
    totalLeads: 0, newLeads: 0, convertedLeads: 0, showroomBookings: 0, showroomDeliveries: 0,
    totalCustomers: 0, pendingWorkflowSteps: 0, holdSteps: 0
  });
  const [funnelData, setFunnelData] = useState<Record<string, { pending: number; hold: number; done: number }>>(overviewStatsCache?.funnel || {});
  const [backlogMap, setBacklogMap] = useState<Record<string, number>>(overviewStatsCache?.backlogMap || {});
  const [loading, setLoading] = useState(!overviewStatsCache);

  const fetchOverviewData = async (silent = false) => {
    try {
      if (!silent && !overviewStatsCache) setLoading(true);
      const res = await apiService.getOverviewStats();
      if (res.success && res.data) {
        setStats(res.data.stats);
        setFunnelData(res.data.funnel);
        setBacklogMap(res.data.backlogMap);
        overviewStatsCache = res.data;
      }
    } catch (err) {
      console.error('Error fetching overview stats', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!overviewStatsCache) {
      fetchOverviewData();
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return <div className="crm-loading"><div className="crm-spinner"></div> Loading overview...</div>;
  }

  return (
    <div>
      <div className="crm-page-header">
        <div className="crm-page-header-left">
          <h2>System Overview</h2>
          <p>Real-time summary of your CRM pipeline</p>
        </div>
        <button className="crm-btn crm-btn-ghost" onClick={() => { overviewStatsCache = null; fetchOverviewData(false); }}>Refresh</button>
      </div>

      {/* Stat Cards */}
      <div className="crm-stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px' }}>
        {statCards.map(card => (
          <div 
            className="crm-stat-card" 
            key={card.key} 
            style={{ 
              borderLeft: `4px solid ${card.textColor}`,
              background: '#FFFFFF',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              padding: '16px'
            }}
          >
            <div className="crm-stat-card-body" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--crm-text-secondary)' }}>{card.label}</span>
                <span style={{ fontSize: '18px' }}>{card.icon}</span>
              </div>
              <div className="stat-value" style={{ color: '#1E293B', fontSize: '24px', fontWeight: 800 }}>
                {stats[card.key] || 0}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Visual Pipeline Backlog Flow */}
      <div style={{ marginTop: '28px', marginBottom: '28px' }}>
        <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          🔍 Pipeline Backlog &amp; Bottleneck Tracker
        </h3>
        <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: 'var(--crm-text-secondary)' }}>
          Indicates where active customers are currently waiting. High numbers highlight workload congestion.
        </p>
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          overflowX: 'auto', 
          padding: '4px 4px 16px 4px',
          scrollbarWidth: 'thin'
        }}>
          {WORKFLOW_STEPS.map((step, idx) => {
            const backlog = backlogMap[step] || 0;
            const isBottleneck = backlog >= 3;
            
            return (
              <div 
                key={step} 
                style={{
                  minWidth: '155px',
                  background: isBottleneck ? '#FFF5F5' : backlog > 0 ? '#F8FAFC' : '#FFFFFF',
                  border: isBottleneck ? '1.5px solid #FCA5A5' : backlog > 0 ? '1px solid #CBD5E1' : '1px solid #E2E8F0',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8' }}>STAGE {idx + 1 < 10 ? `0${idx + 1}` : idx + 1}</span>
                  {isBottleneck && (
                    <span 
                      style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        background: '#EF4444',
                        animation: 'crm-pulse 1.8s infinite'
                      }} 
                    />
                  )}
                </div>
                <div style={{ fontSize: '12.5px', fontWeight: 650, color: '#1E293B', marginBottom: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={step}>
                  {step}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                  <span style={{ fontSize: '22px', fontWeight: 800, color: isBottleneck ? '#DC2626' : backlog > 0 ? '#1E293B' : '#64748B' }}>
                    {backlog}
                  </span>
                  <span style={{ fontSize: '11px', color: '#64748B', fontWeight: 500 }}>waiting</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Workflow Funnel */}
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--crm-text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Workflow Funnel — Status by Department
        </div>
        <div className="crm-table-wrapper">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Department / Step</th>
                <th>Pending</th>
                <th>On Hold</th>
                <th>Done</th>
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {WORKFLOW_STEPS.map(step => {
                const d = funnelData[step] || { pending: 0, hold: 0, done: 0 };
                const total = d.pending + d.hold + d.done;
                const pct = total ? Math.round((d.done / total) * 100) : 0;
                return (
                  <tr key={step}>
                    <td style={{ fontWeight: 600 }}>{step}</td>
                    <td style={{ color: '#92400E', fontWeight: d.pending > 0 ? 700 : 400 }}>{d.pending || '—'}</td>
                    <td style={{ color: '#991B1B', fontWeight: d.hold > 0 ? 700 : 400 }}>{d.hold || '—'}</td>
                    <td style={{ color: 'var(--crm-success)', fontWeight: d.done > 0 ? 700 : 400 }}>{d.done || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 5, background: 'var(--crm-border)', borderRadius: 3 }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? 'var(--crm-success)' : 'var(--crm-primary)', borderRadius: 3, transition: 'width 0.3s' }} />
                        </div>
                        <span style={{ fontSize: '11.5px', color: 'var(--crm-text-muted)', width: 30 }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
